import React, { useEffect, useMemo, useState } from "react";
import { Card, Empty, Field, Stat, StageChip } from "../../dashboard/primitives";
import {
  ACCOUNT_ROLES,
  addMember,
  createAccount,
  deleteAccount,
  listAccounts,
  listMembers,
  removeMember,
  setMemberPassword,
  updateMemberRole,
} from "../../lib/accounts";

const errText = (e) => e?.response?.data?.error || e?.message || "Something went wrong";

// A readable temp password the admin can share; the member changes it later.
const genPassword = () =>
  "bboy-" + Math.random().toString(36).slice(2, 8) + Math.floor(10 + Math.random() * 89);

// ─── Create-account form ──────────────────────────────────────────────────────
const NewAccountForm = ({ onCreated }) => {
  const [name, setName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerDisplayName, setOwnerDisplayName] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      const res = await createAccount({ name, ownerEmail, ownerDisplayName, ownerPassword });
      setNote(
        res.passwordSet
          ? `Account created. ${ownerEmail} can sign in now — email: ${ownerEmail}, temp password: ${ownerPassword}. Share it with them; they can change it after signing in.`
          : `Account created. ${res.ownerCreated ? "Set a temp password for the owner from the member list below." : "Owner already had a login."}`
      );
      setName("");
      setOwnerEmail("");
      setOwnerDisplayName("");
      setOwnerPassword("");
      onCreated?.();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ display: "grid", gap: 12, marginBottom: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Field label="Account name">
          <input className="fw-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="MoBros" required />
        </Field>
        <Field label="Owner name">
          <input className="fw-input" value={ownerDisplayName} onChange={(e) => setOwnerDisplayName(e.target.value)} placeholder="Optional" />
        </Field>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, alignItems: "end" }}>
        <Field label="Owner email">
          <input className="fw-input" type="email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} placeholder="owner@mobros.com" required />
        </Field>
        <Field label="Temp password (they can change it later)">
          <div style={{ display: "flex", gap: 6 }}>
            <input className="fw-input" type="text" value={ownerPassword} onChange={(e) => setOwnerPassword(e.target.value)} placeholder="min 6 characters" minLength={6} required style={{ flex: 1 }} />
            <button type="button" className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px", whiteSpace: "nowrap" }} onClick={() => setOwnerPassword(genPassword())}>
              Generate
            </button>
          </div>
        </Field>
      </div>
      {error && <div style={{ color: "var(--fw-red)", fontSize: 12, fontFamily: "var(--fw-mono)" }}>{error}</div>}
      {note && <div style={{ color: "var(--fw-muted)", fontSize: 12 }}>{note}</div>}
      <div>
        <button type="submit" className="fw-btn fw-btn--primary" disabled={busy}>
          {busy ? "Creating…" : "Create account"}
        </button>
      </div>
    </form>
  );
};

// ─── Member roster + add-member for one account ───────────────────────────────
const MembersPanel = ({ accountId }) => {
  const [members, setMembers] = useState(null);
  const [error, setError] = useState(null);
  const [busyUid, setBusyUid] = useState(null);
  // add-member form
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("editor");
  const [password, setPassword] = useState("");
  const [adding, setAdding] = useState(false);
  const [note, setNote] = useState(null);

  const refresh = async () => {
    try {
      setMembers(await listMembers(accountId));
    } catch (err) {
      setError(errText(err));
    }
  };
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const onAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    setError(null);
    setNote(null);
    try {
      const res = await addMember(accountId, { email, displayName: name, role, password });
      setNote(
        res.passwordSet
          ? `${email} added — they can sign in now with the temp password: ${password}. Share it with them.`
          : res.created
          ? "Login created — set a temp password for them with “Set password”."
          : "Existing login added to this account."
      );
      setEmail("");
      setName("");
      setRole("editor");
      setPassword("");
      await refresh();
    } catch (err) {
      setError(errText(err));
    } finally {
      setAdding(false);
    }
  };

  const changeRole = async (uid, nextRole) => {
    setBusyUid(uid);
    setError(null);
    try {
      await updateMemberRole(accountId, uid, nextRole);
      await refresh();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusyUid(null);
    }
  };

  const onRemove = async (uid, label) => {
    if (!window.confirm(`Remove ${label} from this account? Their login is disabled but their history is kept.`)) return;
    setBusyUid(uid);
    setError(null);
    try {
      await removeMember(accountId, uid);
      await refresh();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusyUid(null);
    }
  };

  const onSetPassword = async (uid, label) => {
    const pw = window.prompt(`Set a new temporary password for ${label} (min 6 characters).\nShare it with them; they can change it after signing in.`);
    if (pw === null) return; // cancelled
    if (pw.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    setBusyUid(uid);
    setError(null);
    setNote(null);
    try {
      await setMemberPassword(accountId, uid, pw);
      setNote(`New temp password set for ${label}: ${pw}`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusyUid(null);
    }
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--fw-line)" }}>
      {error && <div style={{ color: "var(--fw-red)", fontSize: 12, fontFamily: "var(--fw-mono)", marginBottom: 8 }}>{error}</div>}
      {note && <div style={{ color: "var(--fw-muted)", fontSize: 12, marginBottom: 8 }}>{note}</div>}

      {members === null ? (
        <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>Loading members…</div>
      ) : members.length === 0 ? (
        <Empty>No members yet</Empty>
      ) : (
        members.map((m) => {
          const busy = busyUid === m.uid;
          return (
            <div
              key={m.uid}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr auto auto",
                gap: 12,
                alignItems: "center",
                padding: "10px 0",
                borderBottom: "1px solid var(--fw-line)",
                opacity: busy ? 0.5 : 1,
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {m.displayName || "—"}
                  {m.disabled && (
                    <span style={{ fontFamily: "var(--fw-mono)", fontSize: 10, color: "var(--fw-muted)", marginLeft: 8 }}>
                      DISABLED
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>{m.email}</div>
              </div>
              <select
                className="fw-select"
                style={{ padding: "6px 8px", fontSize: 12 }}
                value={m.role || "editor"}
                disabled={busy}
                onChange={(e) => changeRole(m.uid, e.target.value)}
              >
                {ACCOUNT_ROLES.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              <button className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px", whiteSpace: "nowrap" }} disabled={busy} onClick={() => onSetPassword(m.uid, m.displayName || m.email)}>
                Set password
              </button>
              <button className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px", color: "#c0392b" }} disabled={busy} onClick={() => onRemove(m.uid, m.displayName || m.email)}>
                Remove
              </button>
            </div>
          );
        })
      )}

      <form onSubmit={onAdd} style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr 0.8fr 1.4fr auto", gap: 8, alignItems: "end", marginTop: 12 }}>
        <Field label="Add member — email">
          <input className="fw-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@mobros.com" required />
        </Field>
        <Field label="Name">
          <input className="fw-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Optional" />
        </Field>
        <Field label="Role">
          <select className="fw-select" value={role} onChange={(e) => setRole(e.target.value)}>
            {ACCOUNT_ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </Field>
        <Field label="Temp password">
          <div style={{ display: "flex", gap: 4 }}>
            <input className="fw-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min 6" minLength={6} required style={{ flex: 1, minWidth: 0 }} />
            <button type="button" className="fw-btn fw-btn--ghost" style={{ padding: "6px 8px", whiteSpace: "nowrap" }} onClick={() => setPassword(genPassword())}>
              Gen
            </button>
          </div>
        </Field>
        <button type="submit" className="fw-btn fw-btn--primary" style={{ height: 38 }} disabled={adding}>
          {adding ? "Adding…" : "Add"}
        </button>
      </form>
    </div>
  );
};

// ─── Section ──────────────────────────────────────────────────────────────────
const AccountsSection = () => {
  const [accounts, setAccounts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const refresh = async () => {
    try {
      setAccounts(await listAccounts());
    } catch (err) {
      setError(errText(err));
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => {
    refresh();
  }, []);

  const memberCount = useMemo(
    () => accounts.reduce((n, a) => n + (a.memberUids?.length || 0), 0),
    [accounts]
  );

  const onDelete = async (a) => {
    const ok = window.confirm(
      `Delete account “${a.name}”?\n\nThis permanently removes the account, all its projects (milestones, tasks, messages), requests, bookings, and the member logins created for it. This cannot be undone.`
    );
    if (!ok) return;
    const typed = window.prompt(`Type the account name to confirm deletion:\n\n${a.name}`);
    if (typed !== a.name) return;
    setBusyId(a.id);
    setError(null);
    setNote(null);
    try {
      const res = await deleteAccount(a.id);
      if (expandedId === a.id) setExpandedId(null);
      setNote(
        `Deleted “${res.name || a.name}” — ${res.projects || 0} project(s), ${res.requests || 0} request(s), ${res.bookings || 0} booking(s), ${res.loginsPurged || 0} login(s) removed.`
      );
      await refresh();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="fw-stat-grid">
        <Stat label="Accounts" value={accounts.length} loading={!loaded} />
        <Stat label="Total members" value={memberCount} loading={!loaded} />
      </div>

      <Card
        title="Accounts"
        action={
          <button className="fw-btn fw-btn--primary" onClick={() => setShowForm((v) => !v)}>
            {showForm ? "Close" : "New account"}
          </button>
        }
      >
        {error && <div style={{ color: "var(--fw-red)", fontSize: 12, fontFamily: "var(--fw-mono)", marginBottom: 8 }}>{error}</div>}
        {note && <div style={{ color: "var(--fw-muted)", fontSize: 12, marginBottom: 8 }}>{note}</div>}
        {showForm && <NewAccountForm onCreated={refresh} />}

        {!loaded ? (
          <div style={{ fontSize: 12, color: "var(--fw-muted)" }}>Loading…</div>
        ) : accounts.length === 0 ? (
          <Empty>No accounts yet — create one to add member logins.</Empty>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {accounts.map((a) => {
              const expanded = expandedId === a.id;
              return (
                <div key={a.id} style={{ border: "1px solid var(--fw-line)", borderRadius: "var(--fw-r-md)", padding: 16, opacity: busyId === a.id ? 0.5 : 1 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 12, alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{a.name}</div>
                      <div style={{ fontFamily: "var(--fw-mono)", fontSize: 11, color: "var(--fw-muted)" }}>
                        {(a.memberUids?.length || 0)} member{(a.memberUids?.length || 0) === 1 ? "" : "s"} · {a.id.slice(0, 6)}
                      </div>
                    </div>
                    <StageChip status="in_progress" />
                    <button className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px" }} disabled={busyId === a.id} onClick={() => setExpandedId(expanded ? null : a.id)}>
                      {expanded ? "Hide" : "Members"}
                    </button>
                    <button className="fw-btn fw-btn--ghost" style={{ padding: "6px 10px", color: "#c0392b" }} disabled={busyId === a.id} onClick={() => onDelete(a)}>
                      Delete
                    </button>
                  </div>
                  {expanded && <MembersPanel accountId={a.id} />}
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </>
  );
};

export default AccountsSection;
